import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { UtensilsCrossed, Clock, MapPin, Phone, Star, Wine, ArrowLeft, Calendar, Users, ChefHat, Leaf, Award } from 'lucide-react';
import { toast } from 'sonner';

const RestaurantDemo = () => {
  const [guests, setGuests] = useState('2');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  
  const handleReservation = (e) => {
    e.preventDefault();
    toast.success('✅ Tisch erfolgreich reserviert!', {
      description: 'Bestätigungs-E-Mail wurde versendet.'
    });
  };

  const menuItems = [
    { category: 'Antipasti', items: [
      { name: 'Bruschetta Classica', desc: 'Geröstetes Brot mit Tomaten, Knoblauch & Basilikum', price: '8,90' },
      { name: 'Carpaccio di Manzo', desc: 'Hauchdpnnes Rindfleisch mit Parmesan & Rücola', price: '14,90' },
      { name: 'Antipasti Misti', desc: 'Gemischte Vorspeisen für 2 Personen', price: '24,90' },
    ]},
    { category: 'Primi Piatti', items: [
      { name: 'Spaghetti Carbonara', desc: 'Original römische Rezeptur mit Guanciale', price: '16,90' },
      { name: 'Risotto ai Funghi Porcini', desc: 'Cremiges Risotto mit Steinpilzen', price: '18,90' },
      { name: 'Tagliatelle al Tartufo', desc: 'Frische Pasta mit schwarzem Trüffel', price: '26,90' },
    ]},
    { category: 'Secondi', items: [
      { name: 'Ossobuco alla Milanese', desc: 'Geschmorte Kalbshaxe mit Safranrisotto', price: '32,90' },
      { name: 'Branzino al Forno', desc: 'Im Ofen gebackener Wolfsbarsch', price: '28,90' },
      { name: 'Bistecca Fiorentina', desc: 'T-Bone Steak vom Chianina Rind (pro 100g)', price: '9,90' },
    ]},
  ];

  const features = [
    { icon: ChefHat, title: 'Küchenchef aus Neapel', desc: 'Authentische Rezepte' },
    { icon: Leaf, title: 'Frische Zutaten', desc: 'Täglich aus Italien' },
    { icon: Wine, title: 'Weinkeller', desc: 'Über 200 Sorten' },
    { icon: Award, title: 'Ausgezeichnet', desc: 'Michelin Empfehlung' },
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Back Link */}
      <div className="fixed top-4 left-4 z-50">
        <Link to="/portfolio">
          <Button variant="outline" className="bg-white/90 backdrop-blur-sm shadow-md">
            <ArrowLeft size={16} className="mr-2" />
            Zurück zum Portfolio
          </Button>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-stone-900/95 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-amber-400 font-serif text-2xl font-bold">Bella Italia</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-stone-300">
            <a href="#menu" className="hover:text-amber-400">Speisekarte</a>
            <a href="#about" className="hover:text-amber-400">Über uns</a>
            <a href="#reservation" className="hover:text-amber-400">Reservieren</a>
          </div>
          <Button className="bg-amber-600 hover:bg-amber-700 text-white">
            <Calendar size={16} className="mr-2" />
            Reservieren
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center bg-stone-900">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1920&q=80')] bg-cover bg-center opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900 via-stone-900/50 to-transparent" />
        
        <div className="relative text-center px-4 py-32">
          <div className="flex justify-center gap-2 mb-6">
            {[...Array(3)].map((_, i) => (
              <Star key={i} className="text-amber-400 fill-amber-400" size={24} />
            ))}
          </div>
          <h1 className="text-6xl sm:text-7xl font-serif font-bold text-white mb-6">
            Bella Italia
          </h1>
          <p className="text-xl text-stone-200 mb-4 font-light tracking-wide">
            Ristorante & Enoteca
          </p>
          <p className="text-stone-400 max-w-xl mx-auto mb-10">
            Seit 1995 servieren wir Ihnen authentische italienische Küche im Herzen Berlins. Buon appetito!
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" onClick={() => document.getElementById('reservation').scrollIntoView({ behavior: 'smooth' })} className="bg-amber-600 hover:bg-amber-700 text-white px-8">
              <Calendar size={18} className="mr-2" />
              Tisch reservieren
            </Button>
            <Button size="lg" onClick={() => document.getElementById('menu').scrollIntoView({ behavior: 'smooth' })} variant="outline" className="border-white/30 text-white hover:bg-white/10">
              Speisekarte ansehen
            </Button>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Info Bar */}
      <section className="py-4 bg-amber-600 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 text-sm">
            <span className="flex items-center gap-2">
              <Clock size={16} />
              Di-So: 12:00 - 23:00 Uhr
            </span>
            <span className="flex items-center gap-2">
              <MapPin size={16} />
              Italienische Str. 42, 10115 Berlin
            </span>
            <span className="flex items-center gap-2">
              <Phone size={16} />
              030 123 456 78
            </span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="about" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {features.map((feature, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="text-amber-600" size={32} />
                </div>
                <h3 className="font-semibold text-stone-900 mb-1">{feature.title}</h3>
                <p className="text-stone-600 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Menu */}
      <section id="menu" className="py-24 bg-stone-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">La Nostra Cucina</span>
            <h2 className="text-4xl font-serif font-bold text-stone-900 mt-3">Speisekarte</h2>
          </div>
          
          <div className="space-y-12">
            {menuItems.map((section, i) => (
              <div key={i}>
                <h3 className="text-2xl font-serif font-bold text-stone-900 mb-6 pb-2 border-b border-amber-300">
                  {section.category}
                </h3>
                <div className="space-y-6">
                  {section.items.map((item, j) => (
                    <div key={j} className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-stone-900">{item.name}</h4>
                        <p className="text-stone-600 text-sm">{item.desc}</p>
                      </div>
                      <span className="text-amber-600 font-semibold whitespace-nowrap ml-4">{item.price} €</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button variant="outline" className="border-amber-600 text-amber-600 hover:bg-amber-50">
              Komplette Speisekarte als PDF
            </Button>
          </div>
        </div>
      </section>

      {/* Reservation */}
      <section id="reservation" className="py-24 bg-stone-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-amber-400 font-semibold text-sm uppercase tracking-wider">Reservierung</span>
            <h2 className="text-4xl font-serif font-bold text-white mt-3">Tisch reservieren</h2>
          </div>
          
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <form className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Name</label>
                    <Input placeholder="Ihr Name" className="bg-stone-50" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Telefon</label>
                    <Input type="tel" placeholder="Ihre Telefonnummer" className="bg-stone-50" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Datum</label>
                    <Input type="date" className="bg-stone-50" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Uhrzeit</label>
                    <select className="w-full h-10 px-3 bg-stone-50 border border-stone-200 rounded-md">
                      <option>18:00</option>
                      <option>18:30</option>
                      <option>19:00</option>
                      <option>19:30</option>
                      <option>20:00</option>
                      <option>20:30</option>
                      <option>21:00</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">Personen</label>
                    <select className="w-full h-10 px-3 bg-stone-50 border border-stone-200 rounded-md">
                      <option>2 Personen</option>
                      <option>3 Personen</option>
                      <option>4 Personen</option>
                      <option>5 Personen</option>
                      <option>6+ Personen</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Besondere Wünsche</label>
                  <textarea className="w-full h-24 px-4 py-3 bg-stone-50 border border-stone-200 rounded-lg resize-none" placeholder="Allergien, Anlass, etc."></textarea>
                </div>
                <Button className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg">
                  Reservierung anfragen
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-stone-900 border-t border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-stone-400">
            <div>
              <span className="text-amber-400 font-serif text-2xl font-bold">Bella Italia</span>
              <p className="mt-4 text-sm">Authentische italienische Küche seit 1995.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Kontakt</h4>
              <p className="text-sm">Italienische Str. 42<br />10115 Berlin<br />Tel: 030 123 456 78</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Öffnungszeiten</h4>
              <p className="text-sm">Di - So: 12:00 - 23:00<br />Montag: Ruhetag</p>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-stone-800 text-center text-stone-500 text-sm">
            © 2024 Bella Italia. Alle Rechte vorbehalten.
          </div>
        </div>
      </footer>

      {/* Demo Badge */}
      <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg z-50">
        Demo-Projekt von Beta-Webagentur
      </div>
    </div>
  );
};

export default RestaurantDemo;
